import type { ColorPalette, GetBrandDTO, Typography } from "@nlc/shared-types";

import {
  BookTypeIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  PaletteIcon,
} from "lucide-react";
import { useCallback, useState } from "react";

import { SectionHeader } from "../SectionHeader";

type ColorsFontsTabProps = {
  brand: Pick<GetBrandDTO, "colorPalette" | "typography">;
};

export function ColorsFontsTab({ brand }: ColorsFontsTabProps) {
  const { colorPalette, typography } = brand;
  const hasColors = colorPalette.length > 0;
  const hasFonts = typography.length > 0;

  if (!hasColors && !hasFonts) {
    return (
      <p className="py-xl text-center text-sm text-text-quaternary">
        No colors or fonts available
      </p>
    );
  }

  return (
    <div className="@container flex flex-col gap-xl">
      {hasColors && (
        <div className="flex flex-col gap-xl">
          <SectionHeader Icon={PaletteIcon} title="Color palette" />
          <div className="grid grid-cols-2 @md:grid-cols-3 gap-xl">
            {colorPalette.map((color) => (
              <ColorSwatch color={color} key={color.id} />
            ))}
          </div>
        </div>
      )}

      {hasFonts && (
        <>
          <div className="h-px w-full bg-border-primary" />
          <div className="flex flex-col gap-xl">
            <SectionHeader Icon={BookTypeIcon} title="Typography" />
            <div className="flex flex-col gap-md">
              {typography.map((font) => (
                <FontCard font={font} key={font.id} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ColorSwatch({ color }: { color: ColorPalette }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(color.hexCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }, [color.hexCode]);

  return (
    <div className="flex items-center gap-md">
      <div
        className="size-10 shrink-0 rounded-md border-2 border-white/20"
        style={{ backgroundColor: color.hexCode }}
      />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-xs">
        <button
          aria-label={copied ? "Copied" : `Copy ${color.hexCode}`}
          className="flex max-w-full items-center gap-xs rounded-full border border-utility-gray-200 bg-utility-gray-50 py-xxs pl-md pr-sm transition-colors hover:bg-utility-gray-100"
          onClick={handleCopy}
          type="button"
        >
          <span className="truncate text-xs font-medium text-utility-gray-700">
            {color.hexCode.toUpperCase()}
          </span>
          {copied ? (
            <CheckIcon className="size-3 shrink-0 text-utility-gray-700" />
          ) : (
            <CopyIcon className="size-3 shrink-0 text-utility-gray-700" />
          )}
        </button>
        {color.purpose && (
          <span className="w-full truncate text-xs text-text-secondary">
            {color.purpose}
          </span>
        )}
      </div>
    </div>
  );
}

function FontCard({ font }: { font: Typography }) {
  const label = formatFontName(font);
  const isGoogle = font.googleFontId != null;
  const isDownloadable = !isGoogle && !!font.customFontUrl;

  const handleDownload = useCallback(() => {
    if (!font.customFontUrl) return;

    const a = document.createElement("a");

    a.href = font.customFontUrl;
    a.download = font.customFontName ?? font.family;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.append(a);
    a.click();
    a.remove();
  }, [font.customFontUrl, font.customFontName, font.family]);

  const googleFontsUrl = isGoogle
    ? `https://fonts.google.com/specimen/${encodeURIComponent(font.family)}`
    : null;

  return (
    <div className="flex items-center gap-md rounded-xl border border-border-secondary bg-bg-primary px-xl py-lg">
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-brand-secondary">
        {label}
      </span>
      {isGoogle && googleFontsUrl ? (
        <a
          aria-label={`View ${font.family} on Google Fonts`}
          className="shrink-0 cursor-pointer rounded-md p-md text-text-brand-secondary hover:bg-bg-secondary"
          href={googleFontsUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          <ExternalLinkIcon size={20} />
        </a>
      ) : (
        isDownloadable && (
          <button
            aria-label={`Download ${label} font`}
            className="shrink-0 cursor-pointer rounded-md p-md text-text-brand-secondary hover:bg-bg-secondary"
            onClick={handleDownload}
            type="button"
          >
            <DownloadIcon size={20} />
          </button>
        )
      )}
    </div>
  );
}

function formatFontName(font: Typography): string {
  const name = [font.family, font.variant]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  return font.hierarchy ? `${name} - ${font.hierarchy}` : name;
}
